import { type SxProps, type Theme } from "@mui/material";

import { FIELD_STACK_SX } from "ui/forms/input-control/input-control.styles";
import { mergeSx } from "ui/merge-sx";
import { type SelectSize } from "ui/select/select.types";

import {
  ACTIONS_GAP,
  CLEAR_ICON_SIZE,
  CONTROL_PADDING,
  fieldIconButtonSx,
} from "theme/components/field-geometry";

export const CONTROL_TYPOGRAPHY: Record<SelectSize, "body1" | "body2"> = {
  medium: "body1",
  small: "body2",
};

/**
 * Figma's `input/{size}/container/pr` — shorter than the left inset because the chevron's 24/20px box
 * already carries whitespace around the glyph.
 */
export const CONTROL_PADDING_RIGHT: Record<SelectSize, number> = { medium: 10, small: 6 };

/** Figma's `input/{size}/container/chevron-icon`. */
export const CHEVRON_SIZE: Record<SelectSize, number> = { medium: 24, small: 20 };

/** Figma's `input/{size}/container/clear-spacing`, on either side of the divider. */
export const CLEAR_SPACING: Record<SelectSize, number> = { medium: 8, small: 6 };

export const DIVIDER_HEIGHT: Record<SelectSize, number> = { medium: 20, small: 16 };

export const DIVIDER_WIDTH = 1;

/**
 * Figma's `input/{size}/multi-select/pl` and `/py`, as a spacing multiplier — 4px at both sizes, so
 * the tags sit close to the border where a single value keeps the full inset.
 */
export const MULTI_SELECT_INSET = 0.5;

/** Figma's `input/{size}/container/multiple-spacing`, between tags and between wrapped rows. */
export const MULTIPLE_SPACING: Record<SelectSize, number> = { medium: 0.75, small: 0.5 };

/**
 * Doubled to outrank MUI's `.MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall`,
 * whose three classes beat a plain `& .MuiOutlinedInput-root` and put MUI's 6px padding back.
 */
export const CONTROL = ".MuiOutlinedInput-root.MuiOutlinedInput-root";

/**
 * MUI keeps the end adornment absolutely positioned, so wrapping multi-select tags can flow under it,
 * and reserves its width as the control's `paddingRight` from the root's `hasPopupIcon` /
 * `hasClearIcon` classes. These rules restate that reservation at Figma's widths: the icons, the
 * divider between them, and `container/spacing` before the value.
 */
export const getControlSx =
  (size: SelectSize, hasStartIcon: boolean, multiple: boolean): SxProps<Theme> =>
  (theme) => {
    const { x, y } = CONTROL_PADDING[size];
    const valueGap = theme.spacing(ACTIONS_GAP[size]);
    const paddingRight = CONTROL_PADDING_RIGHT[size];
    const chevron = CHEVRON_SIZE[size];
    const clear = CLEAR_ICON_SIZE[size];
    const clearSpacing = CLEAR_SPACING[size];
    const reserve = (icons: number) => `calc(${paddingRight + icons}px + ${valueGap})`;
    // Restated rather than left to the theme: MUI's Autocomplete pads its control with a selector that
    // out-ranks the `MuiOutlinedInput` geometry. `startIcon` sits inside, in a start `InputAdornment`.
    const paddingLeft = theme.spacing(x);

    /**
     * A multi-select pulls the control in to `multi-select/pl` / `py` and hands the difference to
     * the input, so an empty one — Figma draws none — still measures as a single select: the input's
     * line plus that padding is the tag's height, which keeps every row of tags the same 40 / 32px.
     * The input is the control's first child exactly when there are no tags (MUI renders them before
     * it), so `:first-child` puts the placeholder back on the full inset, and its absence hides the
     * placeholder MUI would otherwise print after the last tag; jsdom cannot match `:has()`.
     */
    const multipleControlSx = {
      paddingTop: theme.spacing(MULTI_SELECT_INSET),
      paddingBottom: theme.spacing(MULTI_SELECT_INSET),
      ...(hasStartIcon ? {} : { paddingLeft: theme.spacing(MULTI_SELECT_INSET) }),
      gap: theme.spacing(MULTIPLE_SPACING[size]),
      // MUI spaces the tags with a 3 / 2px margin of their own; the control's `gap` replaces it.
      "& .MuiAutocomplete-tag": { margin: 0, maxWidth: "100%" },
      // The icon is a flex item beside the tags, so the control's `gap` already stands between them.
      "& .MuiInputAdornment-positionStart": {
        marginRight: theme.spacing(ACTIONS_GAP[size] - MULTIPLE_SPACING[size]),
      },
    };
    const multipleInputSx = {
      paddingTop: theme.spacing(y - MULTI_SELECT_INSET),
      paddingBottom: theme.spacing(y - MULTI_SELECT_INSET),
      ...(hasStartIcon
        ? {}
        : { "&:first-child": { paddingLeft: theme.spacing(x - MULTI_SELECT_INSET) } }),
      "&:not(:first-child)::placeholder": { opacity: 0 },
    };

    return {
      [`& ${CONTROL}`]: {
        ...theme.typography[CONTROL_TYPOGRAPHY[size]],
        paddingTop: theme.spacing(y),
        paddingBottom: theme.spacing(y),
        paddingLeft,
        paddingRight: `${paddingRight}px`,
        ...(multiple ? multipleControlSx : {}),
        /**
         * The box is sized by the padding and the type step's line-height. MUI pads the input itself
         * and pins `height: 1.4375em`, which would measure 39px / 32.125px whatever the line says.
         */
        "& .MuiAutocomplete-input.MuiAutocomplete-input": {
          padding: 0,
          height: "auto",
          ...(multiple ? multipleInputSx : {}),
        },
        "& .MuiAutocomplete-endAdornment": {
          right: paddingRight,
          display: "flex",
          alignItems: "center",
          gap: `${clearSpacing}px`,
        },
        /** Both buttons share the field-wide hover circle, whatever their glyph sizes. */
        "& .MuiAutocomplete-clearIndicator": {
          order: 0,
          ...fieldIconButtonSx(size, clear),
        },
        "& .MuiAutocomplete-popupIndicator": {
          order: 2,
          ...fieldIconButtonSx(size, chevron),
          "& svg": { width: chevron, height: chevron },
        },
      },
      /**
       * MUI keeps the input at `min-width: 30px` so there is room to type a filter. Behind tags and out
       * of focus it is empty and invisible, yet that width, plus the gap before it, still wrapped it
       * onto a blank second row whenever the tags left less than 36px. Collapsed until focused, it
       * never forces a row; focus hands the width back, and the wrap with it if there is no room.
       */
      ...(multiple
        ? {
            [`&:not(.Mui-focused) ${CONTROL} .MuiAutocomplete-input.MuiAutocomplete-input:not(:first-child)`]:
              { minWidth: 0, marginLeft: theme.spacing(-MULTIPLE_SPACING[size]) },
          }
        : {}),
      [`&.MuiAutocomplete-hasPopupIcon ${CONTROL}`]: { paddingRight: reserve(chevron) },
      [`&.MuiAutocomplete-hasClearIcon ${CONTROL}`]: { paddingRight: reserve(clear) },
      [`&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon ${CONTROL}`]: {
        paddingRight: reserve(clear + clearSpacing + DIVIDER_WIDTH + clearSpacing + chevron),
        /**
         * A pseudo-element because the end adornment's children are MUI's two buttons, with no slot
         * between them; as a flex item of the adornment, `order` puts it in the middle. jsdom cannot
         * read pseudo-element styles, so its geometry is checked in a browser, not in the suite.
         */
        "& .MuiAutocomplete-endAdornment::before": {
          content: '""',
          order: 1,
          // A bare `1` would be read by `sx` as a fraction, i.e. `100%`.
          width: `${DIVIDER_WIDTH}px`,
          height: DIVIDER_HEIGHT[size],
          backgroundColor: theme.palette.divider,
        },
      },
    };
  };

const FIELD_ROOT_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", width: "100%" };

export const FIELD_SX: Record<SelectSize, SxProps<Theme>> = {
  medium: mergeSx(FIELD_ROOT_SX, FIELD_STACK_SX.medium),
  small: mergeSx(FIELD_ROOT_SX, FIELD_STACK_SX.small),
};

/** The control and the call site's `actionTrigger`, side by side. */
export const FIELD_ROW_SX: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  minWidth: 0,
};

/** The clipped-value tooltip's anchor fills the row, so the control keeps its full width. */
export const VALUE_TOOLTIP_WRAPPER_SX: SxProps<Theme> = {
  display: "block",
  flex: "1 1 0%",
  minWidth: 0,
};

/** The tooltip's anchor round the control. */
export const CONTROL_FRAME_SX: SxProps<Theme> = {
  position: "relative",
  width: "100%",
  minWidth: 0,
};

/** MUI lays the option out as a flex row; this spaces the checkbox, the icon and the label. */
export const OPTION_SX: SxProps<Theme> = { gap: 1 };

/** A multi-select row's checkbox, flush with the row: `SwitchBase`'s 9px padding would pad the list. */
export const OPTION_CHECKBOX_SX: SxProps<Theme> = { p: 0, mr: 1 };

export const OPTION_CONTENT_SX: SxProps<Theme> = {
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
  minWidth: 0,
};

export const OPTION_MAIN_SX: SxProps<Theme> = {
  display: "flex",
  flex: "1 1 0%",
  alignItems: "center",
  gap: 1,
  minWidth: 0,
};

export const OPTION_ICON_SX: SxProps<Theme> = {
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
};

/** A text label wraps; a node label is clipped, since it may not be text at all. */
export const OPTION_TEXT_LABEL_SX: SxProps<Theme> = {
  flex: "1 1 0%",
  minWidth: 0,
  overflowWrap: "break-word",
};

export const OPTION_NODE_LABEL_SX: SxProps<Theme> = {
  display: "flex",
  flex: "1 1 0%",
  alignItems: "center",
  minWidth: 0,
  overflow: "hidden",
};

/** The glyph an option with `isAlertShown` carries, in the list and in the closed control. */
export const ALERT_ICON_SX: SxProps<Theme> = (theme) => ({
  width: 20,
  height: 20,
  flexShrink: 0,
  color: theme.palette.error.main,
});

export const OPTION_ALERT_ICON_SX: SxProps<Theme> = mergeSx(ALERT_ICON_SX, { ml: 1 });

/** In the closed control the field's geometry sizes it, as it does every icon there; only the colour is its own. */
export const VALUE_ALERT_ICON_SX: SxProps<Theme> = (theme) => ({ color: theme.palette.error.main });

/** The full text of a tag cut at `MAX_CHIP_LABEL_LENGTH`. */
export const CHIP_TOOLTIP_MAX_WIDTH = 320;
